import { useField } from 'formik';
import { useRef, useMemo } from 'react';

import { CommonFieldProps } from 'types';
import { controlError } from 'utils/error';
import { getSelectValue } from 'utils/helpers';
import { Select, SelectOption, AutocompleteSelect } from 'components/select';

import { SelectProps } from '../select';

export interface SelectFieldProps
  extends Omit<SelectProps, 'name'>, CommonFieldProps {
  onlyValue?: boolean;
  clearToNull?: boolean;
  isFirstStep?: boolean;
  isAutocomplete?: boolean;
}

export const SelectField = ({
  name,
  options,
  onChange,
  onlyValue,
  clearToNull,
  isCreatable,
  isFirstStep,
  isAutocomplete,
  controlErrorLabel = true,
  ...props
}: SelectFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const value = useMemo(
    () =>
      getSelectValue({ options, onlyValue, isCreatable, value: field.value }),
    [onlyValue, options, field.value, isCreatable]
  );

  const error = controlError(meta, name, props.label, controlErrorLabel);

  // A select counts as touched once the person used it (opened its menu,
  // typed or picked), not when focus only passed through it: a dialog that
  // focused it on open showed "Employee is a required field" as soon as
  // anything else was clicked (owner pass 8.5). Submit still touches every
  // field (Formik), so required fields still say so then.
  const used = useRef(false);

  const Component = isAutocomplete ? AutocompleteSelect : Select;

  return (
    <Component
      {...field}
      {...props}
      error={error}
      value={value}
      options={options}
      isCreatable={isCreatable}
      onMenuOpen={() => {
        used.current = true;
        props.onMenuOpen?.();
      }}
      onBlur={(event) => {
        if (used.current) {
          field.onBlur(event);
        }
        props.onBlur?.(event);
      }}
      onChange={async (option, action) => {
        used.current = true;
        const changeValue = async () => {
          let newValue;

          if (clearToNull && action.action === 'clear') {
            newValue = null;
          } else {
            newValue = onlyValue
              ? Array.isArray(option)
                ? option.map((item) => item.value)
                : // @ts-ignore
                  option?.value
              : option;
          }

          if (
            !isFirstStep ||
            (isFirstStep && (option as SelectOption)?.isSingle)
          ) {
            await helpers.setValue(newValue);
          }

          onChange?.(option, action);
        };

        await changeValue();
      }}
    />
  );
};
